import { api_v2 } from "./api"

export interface AuditoriaParams {
  dataInicio: string
  dataFim: string
  mentorId?: number | null
  empId?: number | null
  tipoMentor?: "ambos" | "principal" | "especialista"
  tipoRelatorio: "analitico" | "sintetico"
}

export interface AuditoriaAnaliticoItem {
  sessaoId: number
  dataHora: string
  mentorId: number
  mentorNome: string
  empId: number
  empresaNome: string
  razaoSocial: string | null
  cnpj: string | null
  tipoMentor: "Principal" | "Especialista"
  tipoSessao: "Presencial" | "Online" | "Outro"
  status: number | null
  tema: string | null
  consumeSaldo: boolean
  valor: number | null
}

export interface AuditoriaSinteticoItem {
  mentorId: number
  mentorNome: string
  empId: number
  empresaNome: string
  razaoSocial: string | null
  cnpj: string | null
  tipoMentor: "Principal" | "Especialista"
  statusGrupo: "Agendada" | "Realizado" | "No-show" | "Cancelado"
  totalSessoes: number
  sessoesPresenciais: number
  sessoesOnline: number
}

export interface AuditoriaResponse<T> {
  data: T[]
  meta: {
    total: number
    tipoRelatorio: "analitico" | "sintetico"
  }
}

export const getAuditoria = async (
  params: AuditoriaParams
): Promise<AuditoriaResponse<AuditoriaAnaliticoItem | AuditoriaSinteticoItem>> => {
  const queryParams: Record<string, string | number> = {
    dataInicio: params.dataInicio,
    dataFim: params.dataFim,
    tipoRelatorio: params.tipoRelatorio,
  }

  if (params.mentorId) queryParams.mentorId = params.mentorId
  if (params.empId) queryParams.empId = params.empId
  if (params.tipoMentor && params.tipoMentor !== "ambos") {
    queryParams.tipoMentor = params.tipoMentor
  }

  const response = await api_v2.get("/mentoria/auditoria", { params: queryParams })
  return {
    data: response.data.data,
    meta: {
      total: response.data.meta?.total ?? response.data.data?.length ?? 0,
      tipoRelatorio: response.data.meta?.tipoRelatorio ?? params.tipoRelatorio,
    },
  }
}

export const getContratosEmpresa = async (
  empId: number
): Promise<{ emprId: number; dataInicio: string; dataFim: string }[]> => {
  const response = await api_v2.get(`/mentoria/auditoria/empresa/${empId}/contratos`)
  return response.data.data ?? []
}

export const getMentoresLista = async (): Promise<{ mentorId: number; nome: string }[]> => {
  const response = await api_v2.get("/mentoria/mentores", { params: { all: true, limit: 500 } })
  return response.data.data ?? []
}

export const getEmpresasLista = async (mentorId?: number): Promise<{ empId: number; nomeFantasia: string; razaoSocial: string | null; cnpj: string | null }[]> => {
  const params = mentorId ? { mentorId } : {}
  const response = await api_v2.get("/mentoria/auditoria/empresas", { params })
  return response.data.data ?? []
}

export interface EmpresaSemSessaoItem {
  empId: number
  nomeFantasia: string
  classificacao: string | null
  contratoInicio: string | null
  contratoFim: string | null
  /** Presentes no modo "Ver por: Empresa" — última sessão de QUALQUER mentor com a empresa. */
  mentorPrincipal?: string | null
  ultimaSessaoValida?: string | null
  diasSemSessao?: number
  /**
   * Presentes no modo "Ver por: Mentor" — 1 linha por vínculo mentor-empresa (Principal e/ou Especialista);
   * a mesma empresa pode aparecer mais de uma vez quando mais de um mentor está além do limite de dias.
   */
  mentorIdAlvo?: number
  mentorNomeAlvo?: string
  ultimaSessaoMentor?: string | null
  diasSemSessaoMentor?: number
  /** Papel do mentor-alvo nesta linha: Principal (empresas.mentor_id) ou Especialista (mentor_especialista). */
  papelMentorAlvo?: "Principal" | "Especialista"
}

export interface SessaoEmpresaItem {
  sessaoId: number
  dataHora: string
  status: number
  mentorNome: string
  tipoMentor: "Principal" | "Especialista" | "Outro"
  contratoInicio: string | null
  contratoFim: string | null
}

export const getEmpresasSemSessao = async (
  diasMinSessao: number,
  diasMaxSessao?: number,
  mentorId?: number,
  verPor?: "empresa" | "mentor"
): Promise<EmpresaSemSessaoItem[]> => {
  const params: Record<string, number | string> = { diasMinSessao }
  if (diasMaxSessao !== undefined) params.diasMaxSessao = diasMaxSessao
  if (mentorId !== undefined) params.mentorId = mentorId
  if (verPor !== undefined) params.verPor = verPor
  const response = await api_v2.get("/mentoria/acompanhamento-clientes", { params })
  return response.data.data ?? []
}

export const getSessoesEmpresa = async (
  empId: number,
  dataInicio?: string,
  dataFim?: string
): Promise<SessaoEmpresaItem[]> => {
  const params: Record<string, string | number> = {}
  if (dataInicio) params.dataInicio = dataInicio
  if (dataFim) params.dataFim = dataFim
  const response = await api_v2.get(`/mentoria/acompanhamento-clientes/empresa/${empId}/sessoes`, { params })
  return response.data.data ?? []
}
